import { useState, useRef } from 'react';
import { X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import { compressIfImage } from '@/lib/imageCompression';
import { computeNextDueDate, toISODate } from '@/lib/dateUtils';
import { parseISO } from 'date-fns';
import type { Reminder } from '@/types';

interface ServiceDoneModalProps {
  reminder: Reminder;
  userId: string;
  onClose: () => void;
  onDone: () => void;
}

export function ServiceDoneModal({ reminder, userId, onClose, onDone }: ServiceDoneModalProps) {
  const [doneDate, setDoneDate] = useState(toISODate(new Date()));
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (f: File) => {
    const compressed = await compressIfImage(f);
    setFile(compressed);
  };

  const submit = async () => {
    setSaving(true);
    try {
      let filePath: string | null = null;

      if (file) {
        const ext = file.name.split('.').pop() ?? 'bin';
        filePath = `${reminder.family_id}/${reminder.item_id}/${Date.now()}.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from('invoices')
          .upload(filePath, file, { contentType: file.type });
        if (uploadErr) { toast.error(uploadErr.message); setSaving(false); return; }
      }

      const { error: logErr } = await supabase.from('service_logs').insert({
        reminder_id: reminder.id,
        item_id: reminder.item_id,
        family_id: reminder.family_id,
        done_date: doneDate,
        notes: notes || null,
        file_path: filePath,
        created_by: userId
      });
      if (logErr) { toast.error(logErr.message); setSaving(false); return; }

      if (reminder.one_time) {
        await supabase.from('reminders').update({ is_active: false }).eq('id', reminder.id);
      } else {
        const next = computeNextDueDate(
          parseISO(reminder.next_due_date),
          reminder.repeat_every,
          reminder.repeat_unit
        );
        await supabase.from('reminders').update({
          next_due_date: toISODate(next),
          last_notified_for: null
        }).eq('id', reminder.id);
      }

      toast.success('Service logged!');
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4 animate-slide-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Mark as Done</h2>
          <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">{reminder.title}</p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Done date</label>
            <input
              type="date"
              value={doneDate}
              onChange={(e) => setDoneDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. replaced oil filter, brake pads checked"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Attach bill/receipt (optional)</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              capture="environment"
            />
            {file
              ? <div className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="truncate">{file.name}</span>
                  <button type="button" onClick={() => setFile(null)} className="text-red-400 hover:text-red-600">×</button>
                </div>
              : <button type="button" onClick={() => fileRef.current?.click()} className="text-sm text-brand-600 hover:text-brand-700 underline">
                  Add photo/PDF
                </button>
            }
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 active:bg-brand-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
